import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KiGeneratedBadge } from '../../components/atoms.js';
import { SidePanel } from '../../components/side-panel.js';
import { ApiError } from '../../lib/api.js';
import { mapApiError } from '../../lib/errors.js';
import {
  type SimplificationExtractReason,
  simplificationsApi,
} from '../../lib/simplifications-api.js';

// Task B3 (#53A, Variante A): SAP-Notes sind hinter me.sap.com nicht frei abrufbar — der Nutzer
// öffnet die Note selbst und fügt den Text hier ein; die KI leitet nur Titel + Kurzfassung ab
// (Variante C „aus dem Gedächtnis" ist ausgeschlossen: ein erfundener Titel in einer
// Abnahme-Dokumentation ist schlimmer als gar keiner). Muster: SidePanel wie DataKindsPanel
// (data-kinds-panel.tsx:14-27).
//
// Der eingefügte Note-Text LEBT NUR im Formularzustand dieses Panels — er geht einmal an
// simplificationsApi.extract() und wird danach verworfen (kein useEffect/Storage, der ihn hält
// über die Extraktion hinaus; er landet nirgends in der DB, im Audit-Log oder in einer Meldung).
export function SimplificationNotePanel({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <SidePanel title={t('simpl.notePanel.title')} onClose={onClose}>
      <SimplificationNotePanelContent projectId={projectId} onClose={onClose} />
    </SidePanel>
  );
}

interface Suggestion {
  title: string;
  description: string;
}

const inputClass =
  'rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary';

// Review I1: eine Meldung JE Ausgang — 'unavailable' ist die einzige, bei der „KI derzeit nicht
// verfügbar" stimmt. 'rejected' bekommt bewusst einen eigenen Text und wird nicht unter
// 'unavailable' verbucht: der Injection-Filter trifft gewöhnliche Prosa (englische SAP-Notes
// enthalten „you are now …" regelmäßig), das ist der wahrscheinlichste Fehlschlag hier — und die
// einzige wirksame Handlung (Auszug kürzen) erfährt die Person nur aus dieser Meldung.
const REASON_MESSAGE: Record<Exclude<SimplificationExtractReason, 'ok'>, string> = {
  unavailable: 'simpl.notePanel.degraded',
  no_content: 'simpl.notePanel.noContent',
  rejected: 'simpl.notePanel.rejected',
};

// Spiegelt SimplificationExtractRequestSchema.max(100_000) — Review M2: die Grenze war nur
// serverseitig da, ein zu langer Text lief in einen 400er mit generischem Toast, der die Länge nie
// erwähnte. Wert bewusst dupliziert statt aus dem Zod-Schema gelesen: die Zahl gehört zur
// Server-Validierung, das Panel zeigt sie nur an.
const MAX_NOTE_CHARS = 100_000;

// Review M1: ein Abzeichen ohne Inhalt ist eine Herkunftsangabe für nichts — bei einer leeren
// KI-Beschreibung war `'' === ''` wahr, die Zeile trug in der Tabelle das ✦ neben „—".
function isAi(value: string, suggested: string | undefined): boolean {
  return suggested !== undefined && value.trim().length > 0 && value === suggested;
}

function SimplificationNotePanelContent({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [sapNote, setSapNote] = useState('');
  const [noteText, setNoteText] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Der KI-Vorschlag, GETRENNT vom editierbaren title/description-State: nur so lässt sich beim
  // Übernehmen feststellen, ob der Nutzer ein Feld nach der Extraktion noch angefasst hat — genau
  // das entscheidet je Feld über title_ai/description_ai (Konsequenz von #74 auch beim ANLEGEN,
  // nicht nur beim späteren PATCH).
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [attempted, setAttempted] = useState(false); // mind. einmal extrahiert (Erfolg ODER Degrade)
  // Review I1: früher ein `degraded`-Boolean — damit meldete das Panel drei verschiedene Ausgänge
  // als denselben, und bei „kein verwertbarer Inhalt" war die Meldung „KI derzeit nicht verfügbar"
  // schlicht falsch (die KI WAR da und hat bewusst geantwortet). Der Grund kommt jetzt vom Server.
  const [reason, setReason] = useState<SimplificationExtractReason | null>(null);
  const [sapNoteError, setSapNoteError] = useState<string | null>(null);
  // Branch-Review B4: das Toast-Opt-out (`meta.errorToast: false`) deckte nur den 409 ab — jeder
  // andere Fehlschlag (500, Netzfehler, 403) erzeugte WEDER Toast NOCH Inline-Meldung: das Panel
  // blieb offen, nichts geschah, der Nutzer klickte erneut. Dieselbe Fehlerklasse wie im
  // Extraktions-Pfad (Fix M3), dort schon beseitigt. Ein Opt-out ist nur zulässig, wenn eine eigene
  // Anzeige es deckt — das ist dieser Zustand.
  const [createError, setCreateError] = useState<string | null>(null);

  const extractMutation = useMutation({
    mutationFn: () => simplificationsApi.extract(projectId, noteText),
    // Der Hinweis steht inline im Panel (mit dem konkreten Grund) — ein zusätzlicher globaler Toast
    // wäre eine zweite, gröbere Meldung für dieselbe Sache.
    meta: { errorToast: false },
    onSuccess: (res) => {
      setAttempted(true);
      setReason(res.reason);
      if (res.result) {
        const next = res.result;
        // Review M4: eine ZWEITE Extraktion darf getippte Korrekturen nicht still verwerfen
        // (dieselbe Fehlerklasse wie der Reset-Effekt-Datenverlust aus Paket B). Überschrieben wird
        // ein Feld nur, wenn es leer ist oder noch wortgleich der vorigen KI-Antwort entspricht —
        // dann gibt es nichts zu verlieren. Was die Person selbst getippt hat, bleibt stehen.
        setTitle((prev) => (prev.trim() === '' || prev === suggestion?.title ? next.title : prev));
        setDescription((prev) =>
          prev.trim() === '' || prev === suggestion?.description ? next.description : prev,
        );
        setSuggestion(next);
      } else {
        // Ehrlicher Degrade (Brief-Entscheidung 5): kein Fehler, nur der Hinweis — die Person füllt
        // die Felder manuell, statt eine Fehlermeldung zu bekommen, die wie ein Absturz wirkt.
        setSuggestion(null);
      }
    },
    // Review M3: ohne diesen Zweig blieb `attempted` bei einem 500er/Netzfehler false — Titel- und
    // Beschreibungsfeld erschienen nie, das Panel bot keinen Ausweg. Ein gescheiterter Transport
    // ist für die Person dasselbe wie ein fehlendes Gateway: die KI kam nicht zustande.
    onError: () => {
      setAttempted(true);
      setSuggestion(null);
      setReason('unavailable');
    },
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['simplifications', projectId] });

  const createMutation = useMutation({
    mutationFn: () =>
      simplificationsApi.create(projectId, {
        title,
        description_html: description.trim() ? description : null,
        sap_note: sapNote.trim() ? sapNote : null,
        // Nur wahr, wenn das Feld seit der Extraktion UNVERÄNDERT blieb — sonst behauptete das
        // Abzeichen später fälschlich "das ist noch der KI-Text".
        title_ai: isAi(title, suggestion?.title),
        description_ai: isAi(description, suggestion?.description),
      }),
    meta: { errorToast: false, successToast: 'feedback.created' },
    onMutate: () => setCreateError(null),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setSapNoteError(mapApiError(t, err));
        return;
      }
      // Alles andere: eigene, sichtbare Meldung statt Stille (siehe createError oben).
      setCreateError(mapApiError(t, err));
    },
  });

  // Deckt sich mit dem Server-Eingabe-Deckel (SimplificationExtractRequestSchema.min(20)/max) — ein
  // zu kurzer Text löst gar nicht erst einen (kostenpflichtigen) LLM-Aufruf aus, ein zu langer
  // liefe in einen 400er, dessen generischer Toast die Länge nie erwähnt (Review M2).
  const tooLong = noteText.length > MAX_NOTE_CHARS;
  const canExtract = noteText.trim().length >= 20 && !tooLong && !extractMutation.isPending;
  const canApply = attempted && title.trim().length > 0 && !createMutation.isPending;

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <label
          className="flex flex-col gap-1 text-xs text-text-muted"
          htmlFor="note-panel-sap-note"
        >
          {t('simpl.sapNote')}
          <input
            id="note-panel-sap-note"
            value={sapNote}
            onChange={(e) => {
              setSapNote(e.target.value);
              setSapNoteError(null);
            }}
            className={inputClass}
          />
        </label>
        {sapNoteError && <div className="text-xs text-status-fail">{sapNoteError}</div>}
      </div>

      <label className="flex flex-col gap-1 text-xs text-text-muted" htmlFor="note-panel-text">
        {t('simpl.notePanel.noteText')}
        <textarea
          id="note-panel-text"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          rows={8}
          className={inputClass}
        />
      </label>
      <p className="text-[11px] text-text-muted">{t('simpl.notePanel.noteTextHint')}</p>
      {/* Review M2: Grenze sichtbar machen, statt sie erst als 400er zu erfahren. */}
      <p className={`text-[11px] ${tooLong ? 'text-status-fail' : 'text-text-muted'}`}>
        {t(tooLong ? 'simpl.notePanel.tooLong' : 'simpl.notePanel.lengthHint', {
          chars: noteText.length,
          max: MAX_NOTE_CHARS,
        })}
      </p>

      <button
        type="button"
        onClick={() => extractMutation.mutate()}
        disabled={!canExtract}
        className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent disabled:cursor-not-allowed disabled:opacity-60"
      >
        {t('simpl.notePanel.extract')}
      </button>

      {/* Review I1: je Ausgang eine eigene, wahrheitsgemäße Meldung. 'no_content' und 'rejected'
          sagen zusätzlich, was hilft (mehr Text bzw. den Auszug kürzen) — unter der pauschalen
          „KI nicht verfügbar"-Meldung schickte die Person denselben Text endlos erneut. */}
      {attempted && reason !== null && reason !== 'ok' && (
        <div className="rounded-btn bg-status-warn/10 px-2 py-1.5 text-xs text-status-warn">
          {t(REASON_MESSAGE[reason])}
        </div>
      )}

      {attempted && (
        <>
          <label className="flex flex-col gap-1 text-xs text-text-muted" htmlFor="note-panel-title">
            <span className="flex items-center gap-2">
              {t('simpl.titleField')}
              {isAi(title, suggestion?.title) && <KiGeneratedBadge />}
            </span>
            <input
              id="note-panel-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputClass}
            />
          </label>
          <label
            className="flex flex-col gap-1 text-xs text-text-muted"
            htmlFor="note-panel-description"
          >
            <span className="flex items-center gap-2">
              {t('catalog.fields.description')}
              {isAi(description, suggestion?.description) && <KiGeneratedBadge />}
            </span>
            <textarea
              id="note-panel-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className={inputClass}
            />
          </label>

          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={!canApply}
            className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t('simpl.notePanel.apply')}
          </button>
          {/* Branch-Review B4: sichtbarer Ausgang für jeden Fehlschlag außer dem 409 (der steht
              beim SAP-Note-Feld, weil er genau dieses Feld betrifft). */}
          {createError && (
            <div role="alert" className="text-xs text-status-fail">
              {createError}
            </div>
          )}
        </>
      )}
    </div>
  );
}
